import React from 'react';
import { SectionWrapper } from './ui/section-wrapper';
import { SectionHeading } from './ui/section-heading';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { IconBox } from './ui/icon-box';
import { Button } from './ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import MasterclassRegistrationForm from './MasterclassRegistrationForm';
import { curriculum } from '../data/curriculum';
import { faqItems } from '../data/faq';

const darkBg = { background: 'linear-gradient(160deg, #07000d 0%, #0f0118 40%, #0a000f 100%)' };

const MasterclassPage: React.FC = () => {
  const scrollToForm = () => document.getElementById('registration-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div>
      {/* Hero — dark */}
      <div style={darkBg}>
        <SectionWrapper className="pt-36 pb-20">
          <div className="text-center">
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-purple-400 mb-10">
              90-Min Masterclass
            </p>
            <h1 className="text-4xl lg:text-[4.25rem] font-bold text-white tracking-[-0.03em] mb-6 leading-[1.08]">
              Learn to build anything<br />
              <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-300 bg-clip-text text-transparent">with AI</span>
            </h1>
            <p className="text-white/40 text-lg max-w-2xl mx-auto leading-relaxed mb-12">
              A focused 90-minute session on the builder mindset, the TRUST framework, and how to think about building with AI in 2026. No technical background needed.
            </p>
            <Button size="lg" onClick={scrollToForm} className="rounded-full font-semibold shadow-2xl shadow-purple-600/20">
              Register Now
            </Button>
          </div>
        </SectionWrapper>
      </div>

      {/* How it works — light */}
      <div className="bg-white">
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading title="How it works" description="90 minutes. No fluff. Just what you need to start thinking and building differently." variant="light" />
          <div className="border border-gray-200 rounded-2xl overflow-hidden flex flex-col md:flex-row">
            {[
              { title: 'The builder mindset', desc: 'Understand how the best builders think about AI and why most people get it wrong.', icon: 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18' },
              { title: 'The TRUST framework', desc: 'A repeatable system for approaching any build with AI, from idea to working output.', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' },
              { title: 'Your next move', desc: 'Leave with clarity on where to start and how to apply it to what you want to build.', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            ].map((item, i, arr) => (
              <div key={i} className={`flex-1 text-center p-6 lg:p-8 ${i < arr.length - 1 ? 'border-b md:border-b-0 md:border-r border-gray-200' : ''}`}>
                <IconBox variant="light" size="lg" className="mx-auto mb-6">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                  </svg>
                </IconBox>
                <h3 className="text-gray-900 font-bold text-base mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </SectionWrapper>
      </div>

      {/* Curriculum — dark */}
      <div style={darkBg}>
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading title="What you'll learn" description="Four focused sections across 90 minutes." variant="dark" />
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible defaultValue="week-0">
              {curriculum.map((week, i) => (
                <AccordionItem key={i} value={`week-${i}`} variant="dark" className="mb-2">
                  <AccordionTrigger className="text-white">
                    <span className="text-purple-400 text-sm font-bold tracking-wider uppercase flex-shrink-0 w-20">{week.week}</span>
                    <span className="flex-1 font-medium text-base">{week.title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-[6.5rem]">
                    <p className="text-white/30 text-sm mb-3">{week.description}</p>
                    <ul className="space-y-1.5">
                      {week.topics.map((t, j) => (
                        <li key={j} className="text-white/40 text-sm flex gap-2"><span className="text-purple-500">-</span>{t}</li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </SectionWrapper>
      </div>

      {/* Deliverables — light */}
      <div className="bg-[#faf8fc]">
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading title="What you'll walk away with" variant="light" className="mb-16" />
          <div className="border border-gray-200 rounded-2xl overflow-hidden flex flex-col sm:flex-row sm:flex-wrap max-w-3xl mx-auto">
            {[
              { n: '01', title: 'A builder\'s mindset', desc: 'A new way of thinking about AI as a collaborator, not a search engine.' },
              { n: '02', title: 'The TRUST framework', desc: 'A repeatable system for using AI to build anything, without getting stuck.' },
              { n: '03', title: 'Real-world examples', desc: 'Understand how products get built with AI through real stories and case studies.' },
              { n: '04', title: 'Your personal action plan', desc: 'A clear first step mapped to your specific idea or business.' },
            ].map((item, i) => (
              <div key={item.n} className={`sm:w-1/2 p-6 ${i < 2 ? 'border-b border-gray-200' : ''} ${i % 2 === 0 ? 'sm:border-r border-gray-200' : ''}`}>
                <Badge variant="default" size="sm" className="mb-3">{item.n}</Badge>
                <h4 className="text-gray-900 font-semibold text-base mb-1">{item.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </SectionWrapper>
      </div>

      {/* Registration — white */}
      <div className="bg-white" id="registration-form">
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading title="Reserve your spot" description="Spots are limited to keep the session interactive. Register and we'll send you the next available date." variant="light" className="mb-14" />
          <MasterclassRegistrationForm variant="light" />
        </SectionWrapper>
      </div>

      {/* FAQ — dark */}
      <div style={darkBg}>
        <SectionWrapper className="py-28 md:py-36">
          <SectionHeading title="Frequently asked questions" variant="dark" />
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible>
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} variant="dark" className="mb-2">
                  <AccordionTrigger className="text-white font-medium text-base">
                    <span className="flex-1">{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/40 text-sm leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </SectionWrapper>
      </div>

      {/* Pricing — light */}
      <div className="bg-white">
        <SectionWrapper className="py-20">
          <Card variant="muted" padding="lg" className="max-w-md mx-auto text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Pricing</h3>
            <p className="text-gray-500 text-sm mb-6">Details announced with the next session date. Register now to get first access.</p>
            <Button onClick={scrollToForm} className="rounded-full px-8 h-11 text-sm font-semibold">Reserve My Spot</Button>
          </Card>
        </SectionWrapper>
      </div>
    </div>
  );
};

export default MasterclassPage;
